import { useState, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Search } from "lucide-react";
import { scrollToId } from "./SmoothScroll";
import { SearchBar } from "./SearchBar";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { BUSINESS } from "../data/business";

const LINKS = [
  ["Home", "/"],
  ["Browse Stones", "/browse"],
  ["Bulk Order", "/bulk-order"],
  ["About Us", "/about"],
];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goContact = () => {
    setOpen(false);
    if (pathname !== "/") { navigate("/"); setTimeout(() => scrollToId("contact"), 450); }
    else scrollToId("contact");
  };

  const linkClass = ({ isActive }) => `nav-link ${isActive ? "text-saffron" : ""}`;

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${scrolled || pathname !== "/" ? "bg-night/85 backdrop-blur-xl border-b border-gold/15" : "bg-transparent border-b border-transparent"}`}
    >
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <span className="relative h-11 w-11 rounded-full overflow-hidden ring-1 ring-gold/40 group-hover:ring-saffron transition-[box-shadow,ring-color] duration-500">
            <img src="/img/logo.jpg" alt="Padmavati Gem & Jewelss lotus logo" className="h-full w-full object-cover scale-[1.9] translate-y-[10%]" />
          </span>
          <span className="leading-none">
            <span className="block font-display text-[1.35rem] tracking-wide text-saffron">Padmavati</span>
            <span className="block text-[9px] uppercase tracking-[0.32em] text-gold/80 mt-1">Gem &amp; Jewelss</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7 xl:gap-9">
          {LINKS.map(([label, to]) => (
            <NavLink key={to} to={to} end data-testid={`nav-link-${label.toLowerCase().replace(/\s+/g, "-")}`} className={linkClass}>{label}</NavLink>
          ))}
          <button data-testid="nav-link-contact" onClick={goContact} className="nav-link">Contact</button>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <SearchBar id="nav-search" className="hidden md:flex w-[200px] lg:w-[220px] xl:w-[300px]" />
          <Link to="/enquiry" data-testid="nav-enquiry-btn" className="btn-gold hidden sm:inline-flex">
            <WhatsAppIcon size={16} /> Enquiry
          </Link>
          <button data-testid="nav-search-toggle" onClick={() => setOpen(true)} className="md:hidden text-saffron p-2" aria-label="Search">
            <Search size={20} />
          </button>
          <button data-testid="nav-menu-toggle" onClick={() => setOpen(!open)} className="lg:hidden text-saffron p-2" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-night/95 backdrop-blur-xl border-b border-gold/15 px-6 py-6 flex flex-col gap-5"
          >
            <SearchBar id="mobile-search" onSubmit={() => setOpen(false)} />
            {LINKS.map(([label, to]) => (
              <Link key={to} to={to} onClick={() => setOpen(false)} data-testid={`mobile-link-${label.toLowerCase().replace(/\s+/g, "-")}`} className="text-left font-display text-2xl text-ivory hover:text-saffron transition-colors">{label}</Link>
            ))}
            <button data-testid="mobile-link-contact" onClick={goContact} className="text-left font-display text-2xl text-ivory hover:text-saffron transition-colors">Contact</button>
            <Link to="/enquiry" onClick={() => setOpen(false)} data-testid="mobile-enquiry-btn" className="btn-gold w-fit mt-2"><WhatsAppIcon size={16} /> Enquiry · {BUSINESS.phone}</Link>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
